import React from "react";
import localforage from "localforage";
import {
  acceptNegotiationTerms,
  isTransientNegotiationFailure,
  newIdempotencyKey,
  NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT,
} from "../../services/negotiationBalance";
import { isNegotiationBalanceTermsPublished } from "../../services/negotiationBalanceTerms";
import { TermsAcceptanceModal } from "./TermsAcceptanceModal";
import { SECURE_KEY_COPY } from "./useTopupFlow";

export type UseTermsAcceptanceOptions = {
  /** The version Payments requires (terms.version). */
  version: string;
  /** `org_payments` create: accepting the conditions is a payments manager's act. */
  canAccept: boolean;
  /** Payments recorded the acceptance. */
  onAccepted: () => void;
  /** The balance may have moved (acceptance recorded, or the version changed): read it again. */
  onBalanceChanged: () => void;
};

export type TermsAcceptance = {
  /** /termos publishes `version`; nothing else can be accepted. */
  published: boolean;
  accepting: boolean;
  /** Application-authored copy only. */
  error?: string;
  accept: () => void;
};

/**
 * POST /terms-acceptance for the published version, with one
 * Idempotency-Key per acceptance attempt (an uncertain answer is retried
 * with it). Shared by the recharge and by the weekly automatic top-up, which
 * both need the acceptance first.
 */
export function useTermsAcceptance({
  version,
  canAccept,
  onAccepted,
  onBalanceChanged,
}: UseTermsAcceptanceOptions): TermsAcceptance {
  const [accepting, setAccepting] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const attemptRef = React.useRef<string | null>(null);
  const busyRef = React.useRef(false);
  const aliveRef = React.useRef(true);
  const callbacksRef = React.useRef({ onAccepted, onBalanceChanged });
  callbacksRef.current = { onAccepted, onBalanceChanged };
  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const published = isNegotiationBalanceTermsPublished(version);

  const accept = React.useCallback(() => {
    if (busyRef.current || !published || !canAccept) return;
    if (!attemptRef.current) {
      try {
        attemptRef.current = newIdempotencyKey();
      } catch {
        setError(SECURE_KEY_COPY);
        return;
      }
    }
    const key = attemptRef.current;
    busyRef.current = true;
    setAccepting(true);
    setError(undefined);
    void (async () => {
      try {
        let jwt: string | null | undefined;
        try {
          jwt = await localforage.getItem<string>("JWT");
        } catch {
          jwt = undefined;
        }
        if (!aliveRef.current) return;
        if (!jwt) {
          setAccepting(false);
          setError("Sua sessão expirou. Entre novamente para continuar.");
          return;
        }
        const result = await acceptNegotiationTerms(jwt, {
          idempotencyKey: key,
          termsVersion: version,
        });
        if (!aliveRef.current) return;
        setAccepting(false);
        if (result.ok) {
          window.dispatchEvent(new Event(NEGOTIATION_BALANCE_READINESS_CHANGED_EVENT));
          attemptRef.current = null;
          callbacksRef.current.onBalanceChanged();
          callbacksRef.current.onAccepted();
          return;
        }
        if (!isTransientNegotiationFailure(result.failure)) attemptRef.current = null;
        if (result.failure.code === "negotiation_terms_version_mismatch") {
          callbacksRef.current.onBalanceChanged();
          setError("A versão das condições mudou. Feche e abra de novo.");
          return;
        }
        if (result.failure.code === "negotiation_fiscal_profile_missing") {
          setError(
            "Complete o CNPJ e a razão social no perfil de faturamento da empresa para abrir o saldo.",
          );
          return;
        }
        setError(
          isTransientNegotiationFailure(result.failure)
            ? "Não foi possível registrar o aceite agora. Tente de novo em instantes."
            : "Não foi possível registrar o aceite. Feche e abra de novo.",
        );
      } finally {
        busyRef.current = false;
      }
    })();
  }, [canAccept, published, version]);

  return { published, accepting, error, accept };
}

export type TermsAcceptanceFlowProps = {
  version: string;
  canAccept: boolean;
  onBalanceChanged: () => void;
  /** `accepted` is true when Payments recorded the acceptance. */
  onClose: (outcome: { accepted: boolean }) => void;
};

/**
 * The conditions acceptance on its own, for the weekly automatic top-up:
 * it closes as soon as Payments records the acceptance.
 * @story skip request lifecycle is covered by CI unit tests; see TermsAcceptanceModal for every visual state
 */
export function TermsAcceptanceFlow({
  version,
  canAccept,
  onBalanceChanged,
  onClose,
}: TermsAcceptanceFlowProps) {
  const terms = useTermsAcceptance({
    version,
    canAccept,
    onBalanceChanged,
    onAccepted: () => onClose({ accepted: true }),
  });
  return (
    <TermsAcceptanceModal
      version={version}
      published={terms.published}
      canAccept={canAccept}
      accepting={terms.accepting}
      error={terms.error}
      onAccept={terms.accept}
      onClose={() => onClose({ accepted: false })}
    />
  );
}
